'use client';

import {useEffect, useState} from 'react';
import {ChevronLeft, ChevronRight, Mail} from 'lucide-react';
import {login} from 'iq-auth/api';
import {completeAuth} from 'iq-auth/post_auth_actions';
import {BackButton} from 'iq-auth/components/back_button';
import {GoogleIcon, LinkedInIcon} from 'iq-auth/components/icons';
import {useOAuthUrls} from 'iq-auth/hooks';
import {OrSeparator} from 'iq-auth/components/or_separator';
import {OAuthLink} from 'iq-auth/components/oauth_link';
import {FormField, inputClass} from 'iq-auth/components/form_field';
import {Button} from '@repo/ui/button';

type View = 'oauth' | 'manual';

export type LoginFormProps = {
  onSuccess?: () => void;
  onBack?: () => void;
  onSwitchToSignup?: () => void;
  cta?: string;
  initialEmail?: string;
  next?: string;
};

export function LoginForm({
  onSuccess,
  onBack,
  onSwitchToSignup,
  cta,
  initialEmail = '',
  next,
}: LoginFormProps) {
  const [view, setView] = useState<View>('oauth');
  const [email, setEmail] = useState(initialEmail);
  const [password, setPassword] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const {googleUrl, linkedInUrl} = useOAuthUrls('login', next);

  useEffect(() => {
    if (initialEmail) setEmail(initialEmail);
  }, [initialEmail]);

  const isValid = email.trim().length > 0 && password.length >= 6;
  const forgotPasswordHref = `/forgot-password${email ? `?e=${encodeURIComponent(email)}` : ''}`;

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    setSubmitting(true);
    try {
      await login(email, password);
      await completeAuth({onSuccess, next});
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Something went wrong. Please try again.');
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className='flex flex-col'>
      <div className='mb-8'>
        {view === 'oauth' && onBack && <BackButton onBack={onBack} />}
        {view === 'manual' && (
          <button
            type='button'
            onClick={() => {
              setView('oauth');
              setError(null);
            }}
            className='flex items-center gap-1 text-xs text-iq-grey-medium hover:text-iq-black transition-colors mb-4'>
            <ChevronLeft className='w-3 h-3' />
            Other log in options
          </button>
        )}
        <h2 className='text-2xl font-bold text-iq-black mb-2'>Welcome back</h2>
        {cta && <p className='text-sm text-iq-grey-medium'>{cta}</p>}
      </div>

      {view === 'oauth' ? (
        <div className='flex flex-col gap-3'>
          <OAuthLink href={googleUrl} icon={<GoogleIcon />} label='Log in with Google' />
          <OAuthLink href={linkedInUrl} icon={<LinkedInIcon />} label='Log in with LinkedIn' />
          <button
            type='button'
            onClick={() => setView('manual')}
            className='flex items-center justify-center gap-3 w-full px-4 py-3 border border-iq-grey-light rounded-lg text-sm font-medium text-iq-grey-medium hover:text-iq-black hover:bg-iq-grey-100 transition-colors'>
            <Mail className='w-[17px] h-[17px] -mt-1' />
            Log in with email
          </button>
          {onSwitchToSignup && (
            <>
              <OrSeparator />
              <Button
                type='button'
                variant={'outline'}
                className='border border-iq-grey-light rounded-lg text-sm font-medium text-iq-grey-medium hover:text-iq-black hover:bg-iq-grey-100 transition-colors'
                onClick={onSwitchToSignup}>
                Sign up for free
              </Button>
            </>
          )}
        </div>
      ) : (
        <form onSubmit={handleSubmit} className='flex flex-col gap-4'>
          <FormField label='Email'>
            <input
              type='email'
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder='you@example.com'
              autoComplete='email'
              required
              className={inputClass}
            />
          </FormField>
          <FormField label='Password'>
            <input
              type='password'
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder='Your password'
              autoComplete='current-password'
              required
              className={inputClass}
            />
          </FormField>
          <a
            href={forgotPasswordHref}
            className='-mt-2 self-end text-xs text-iq-grey-medium hover:text-iq-black transition-colors'>
            Forgot password?
          </a>
          {error && <p className='text-xs text-red-500'>{error}</p>}
          <button
            type='submit'
            disabled={!isValid || submitting}
            className='flex items-center justify-center gap-2 px-6 py-3 bg-iq-red text-white text-sm font-semibold rounded-lg hover:opacity-90 transition-opacity disabled:opacity-40 mt-2'>
            <ChevronRight className='w-4 h-4' />
            {submitting ? 'Logging in…' : 'Log in'}
          </button>
        </form>
      )}
    </div>
  );
}
